"use client";

import { FormEvent, useEffect, useState } from "react";
import { ArrowLeft, KeyRound, Loader2, Lock, Mail } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { AuthAccessDenied, AuthError, AuthNotice, FieldIcon } from "./shared-ui";

type SignInStep = "identifier" | "access-denied" | "password" | "magic-code" | "password-reset";

type WorkosSignInFormProps = {
	returnTo: string;
	externalAuthId?: string;
	initialError?: string | null;
	initialNotice?: string | null;
};

type AuthMethod = "sso" | "password" | "email_code";

type LoginResponse = {
	redirectTo?: string;
	error?: string;
};

type MagicStartResponse = {
	ok?: boolean;
	accessDenied?: boolean;
	authMethod?: "sso" | "select";
	authMethods?: AuthMethod[];
	redirectTo?: string;
	ssoRedirectTo?: string;
	ssoOrganization?: {
		name: string;
		logo: string | null;
	};
	error?: string;
	message?: string;
};

const MAGIC_CODE_RESEND_COOLDOWN_SECONDS = 60;

function normalize_email(value: string): string {
	return value.trim().toLowerCase();
}

function is_valid_email(value: string): boolean {
	return value.includes("@");
}

function normalize_code(value: string): string {
	return value.replace(/\D/g, "").slice(0, 6);
}

function follow_redirect(payload: { redirectTo?: string } | null): boolean {
	if (!payload?.redirectTo) return false;
	window.location.assign(payload.redirectTo);
	return true;
}

export function WorkosSignInForm({ returnTo, externalAuthId, initialError = null, initialNotice = null }: WorkosSignInFormProps) {
	const [step, set_step] = useState<SignInStep>("identifier");
	const [email, set_email] = useState("");
	const [password, set_password] = useState("");
	const [code, set_code] = useState("");
	const [is_submitting, set_is_submitting] = useState(false);
	const [resend_seconds, set_resend_seconds] = useState(0);
	const [available_auth_methods, set_available_auth_methods] = useState<AuthMethod[]>(["password", "email_code"]);
	const [sso_redirect_to, set_sso_redirect_to] = useState<string | null>(null);
	const [sso_organization, set_sso_organization] = useState<MagicStartResponse["ssoOrganization"]>(undefined);
	const [error, set_error] = useState<string | null>(initialError);
	const [notice, set_notice] = useState<string | null>(initialNotice);

	const is_disabled = is_submitting;
	const normalized_email = normalize_email(email);

	useEffect(() => {
		if (resend_seconds <= 0) return;

		const timer = window.setTimeout(() => {
			set_resend_seconds((current) => Math.max(0, current - 1));
		}, 1000);

		return () => window.clearTimeout(timer);
	}, [resend_seconds]);

	async function handle_identifier_submit(event: FormEvent<HTMLFormElement>) {
		event.preventDefault();
		set_error(null);
		set_notice(null);
		if (!is_valid_email(normalized_email)) {
			set_error("Enter your university email address.");
			return;
		}

		set_is_submitting(true);

		try {
			const response = await fetch("/api/auth/workos/magic/start", {
				method: "POST",
				headers: { "Content-Type": "application/json" },
				body: JSON.stringify({
					email: normalized_email,
					returnTo,
					ssoOnly: true
				})
			});

			const payload = (await response.json().catch(() => null)) as MagicStartResponse | null;
			if (payload?.accessDenied) {
				set_email(normalized_email);
				set_step("access-denied");
				return;
			}
			if (follow_redirect(payload)) return;

			if (!response.ok || !payload?.ok) {
				set_error(payload?.error ?? "We could not check that email address. Please try again.");
				return;
			}

			const sso_redirect = payload.ssoRedirectTo ?? payload.redirectTo;
			if (payload.authMethods?.length === 1 && payload.authMethods[0] === "sso" && sso_redirect) {
				window.location.assign(sso_redirect);
				return;
			}

			set_available_auth_methods(payload.authMethods?.length ? payload.authMethods : ["password", "email_code"]);
			set_sso_redirect_to(payload.ssoRedirectTo ?? null);
			set_sso_organization(payload.ssoOrganization);
			set_email(normalized_email);
			set_step("password");
		} catch {
			set_error("We could not reach the sign-in service. Please try again.");
		} finally {
			set_is_submitting(false);
		}
	}

	async function handle_password_submit(event: FormEvent<HTMLFormElement>) {
		event.preventDefault();
		set_error(null);
		if (!available_auth_methods.includes("password")) return;
		set_is_submitting(true);

		try {
			const response = await fetch("/api/auth/workos/password", {
				method: "POST",
				headers: { "Content-Type": "application/json" },
				body: JSON.stringify({
					email: normalized_email,
					password,
					returnTo,
					externalAuthId
				})
			});

			const payload = (await response.json().catch(() => null)) as LoginResponse | null;
			if (follow_redirect(payload)) return;

			if (!response.ok || !payload?.redirectTo) {
				set_error(payload?.error ?? "We could not sign you in with those details.");
				return;
			}
		} catch {
			set_error("We could not reach the sign-in service. Please try again.");
		} finally {
			set_is_submitting(false);
		}
	}

	function handle_sso_submit() {
		if (!sso_redirect_to) return;
		window.location.assign(sso_redirect_to);
	}

	async function request_magic_code() {
		set_error(null);
		set_notice(null);

		if (!is_valid_email(normalized_email)) {
			set_error("Enter your university email address.");
			return;
		}

		set_is_submitting(true);

		try {
			const response = await fetch("/api/auth/workos/magic/start", {
				method: "POST",
				headers: { "Content-Type": "application/json" },
				body: JSON.stringify({
					email: normalized_email,
					returnTo
				})
			});

			const payload = (await response.json().catch(() => null)) as MagicStartResponse | null;
			if (payload?.accessDenied) {
				set_password("");
				set_code("");
				set_step("access-denied");
				return;
			}
			if (follow_redirect(payload)) return;

			if (!response.ok || !payload?.ok) {
				set_error(payload?.error ?? "We could not send a sign-in code. Please try again.");
				return;
			}

			set_email(normalized_email);
			set_password("");
			set_code("");
			set_step("magic-code");
			set_notice(`We sent a six digit code to ${normalized_email}. It expires in 10 minutes.`);
			set_resend_seconds(MAGIC_CODE_RESEND_COOLDOWN_SECONDS);
		} catch {
			set_error("We could not reach the sign-in service. Please try again.");
		} finally {
			set_is_submitting(false);
		}
	}

	async function request_password_reset() {
		set_error(null);
		set_notice(null);

		if (!is_valid_email(normalized_email)) {
			set_error("Enter your university email address.");
			return;
		}

		set_is_submitting(true);

		try {
			const response = await fetch("/api/auth/workos/password-reset/start", {
				method: "POST",
				headers: { "Content-Type": "application/json" },
				body: JSON.stringify({
					email: normalized_email
				})
			});
			const payload = (await response.json().catch(() => null)) as MagicStartResponse | null;

			if (!response.ok || !payload?.ok) {
				set_error(payload?.error ?? "We could not send a reset email. Please try again.");
				return;
			}

			set_notice(payload.message ?? "If that account exists, we sent a password reset email.");
		} catch {
			set_error("We could not reach the sign-in service. Please try again.");
		} finally {
			set_is_submitting(false);
		}
	}

	async function handle_password_reset_submit(event: FormEvent<HTMLFormElement>) {
		event.preventDefault();
		await request_password_reset();
	}

	async function handle_magic_submit(event: FormEvent<HTMLFormElement>) {
		event.preventDefault();
		set_error(null);
		set_notice(null);
		set_is_submitting(true);

		try {
			const response = await fetch("/api/auth/workos/magic/verify", {
				method: "POST",
				headers: { "Content-Type": "application/json" },
				body: JSON.stringify({
					email: normalized_email,
					code,
					returnTo,
					externalAuthId
				})
			});

			const payload = (await response.json().catch(() => null)) as LoginResponse | null;
			if (follow_redirect(payload)) return;

			if (!response.ok || !payload?.redirectTo) {
				set_error(payload?.error ?? "That code is invalid or has expired.");
				return;
			}
		} catch {
			set_error("We could not reach the sign-in service. Please try again.");
		} finally {
			set_is_submitting(false);
		}
	}

	if (step === "identifier") {
		return (
			<form
				onSubmit={handle_identifier_submit}
				className="space-y-4"
			>
				<AuthError message={error} />
				<AuthNotice message={notice} />
				{externalAuthId ?
					<AuthNotice message="Finish signing in to approve the connected app request." />
				:	null}
				<div className="space-y-2">
					<Label htmlFor="identifier-email">University email</Label>
					<div className="relative">
						<FieldIcon>
							<Mail className="size-4" />
						</FieldIcon>
						<Input
							id="identifier-email"
							type="email"
							value={email}
							onChange={(event) => set_email(event.target.value)}
							autoComplete="email"
							className="h-11 rounded-lg bg-white pl-10 text-base"
							placeholder="name@university.ac.uk"
							required
						/>
					</div>
				</div>
				<Button
					type="submit"
					disabled={is_disabled}
					className="h-11 w-full rounded-lg bg-[#235a96] text-white hover:bg-[#1d4d82]"
				>
					Continue
				</Button>
			</form>
		);
	}

	if (step === "access-denied") {
		return (
			<div className="space-y-4">
				<div className="flex min-h-11 items-center gap-3 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-base text-slate-900">
					<span className="text-slate-400">
						<Mail className="size-4" />
					</span>
					<span className="min-w-0 truncate">{email}</span>
				</div>
				<AuthAccessDenied />
				<Button
					type="button"
					variant="ghost"
					className="h-10 w-full text-slate-600"
					onClick={() => {
						set_error(null);
						set_notice(null);
						set_email("");
						set_step("identifier");
					}}
				>
					<ArrowLeft className="size-4" />
					Use a different email
				</Button>
			</div>
		);
	}

	if (step === "password") {
		return (
			<form
				onSubmit={handle_password_submit}
				className="space-y-4"
			>
				<AuthError message={error} />
				<AuthNotice message={notice} />
				<div className="space-y-2">
					<div className="flex items-center justify-between gap-3">
						<Label>University email</Label>
						<button
							type="button"
							onClick={() => {
								set_error(null);
								set_notice(null);
								set_password("");
								set_available_auth_methods(["password", "email_code"]);
								set_sso_redirect_to(null);
								set_sso_organization(undefined);
								set_step("identifier");
							}}
							className="text-sm font-semibold text-[#235a96] hover:text-[#1d4d82]"
						>
							Use a different email
						</button>
					</div>
					<div className="flex min-h-11 items-center gap-3 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-base text-slate-900">
						<span className="text-slate-400">
							<Mail className="size-4" />
						</span>
						<span className="min-w-0 truncate">{email}</span>
					</div>
				</div>
				{available_auth_methods.includes("password") ?
					<>
						<div className="space-y-2">
							<div className="flex items-center justify-between gap-3">
								<Label htmlFor="password">Password</Label>
								<button
									type="button"
									onClick={() => {
										set_error(null);
										set_notice(null);
										set_password("");
										set_step("password-reset");
									}}
									className="text-sm font-semibold text-[#235a96] hover:text-[#1d4d82]"
								>
									Forgot password?
								</button>
							</div>
							<div className="relative">
								<FieldIcon>
									<Lock className="size-4" />
								</FieldIcon>
								<Input
									id="password"
									type="password"
									value={password}
									onChange={(event) => set_password(event.target.value)}
									autoComplete="current-password"
									className="h-11 rounded-lg bg-white pl-10 text-base"
									placeholder="Enter your password"
									required
								/>
							</div>
						</div>
						<Button
							type="submit"
							disabled={is_disabled}
							className="h-11 w-full rounded-lg bg-[#235a96] text-white hover:bg-[#1d4d82]"
						>
							{is_submitting ? <Loader2 className="size-4 animate-spin" /> : null}
							Sign in
						</Button>
					</>
				:	null}
				{available_auth_methods.includes("email_code") ?
					<Button
						type="button"
						variant="outline"
						disabled={is_disabled}
						onClick={() => request_magic_code()}
						className="h-11 w-full rounded-lg border-slate-300 text-slate-700 hover:bg-slate-50"
					>
						{is_submitting ? <Loader2 className="size-4 animate-spin" /> : <KeyRound className="size-4" />}
						Email me a sign-in code
					</Button>
				:	null}
				{available_auth_methods.includes("sso") && sso_redirect_to ?
					<Button
						type="button"
						variant="outline"
						disabled={is_disabled}
						onClick={handle_sso_submit}
						aria-label={sso_organization?.logo ? `Sign in with ${sso_organization.name}` : "Sign in with SSO"}
						className="h-11 w-full rounded-lg border-slate-300 text-slate-700 hover:bg-slate-50"
					>
						{sso_organization?.logo ?
							<>
								<span>Sign in with</span>
								<img
									src={sso_organization.logo}
									alt=""
									aria-hidden
									className="h-6 max-w-36 shrink-0 object-contain"
								/>
							</>
						:	<>
								<Lock className="size-4" />
								<span>Sign in with SSO</span>
							</>
						}
					</Button>
				:	null}
				<Button
					type="button"
					variant="ghost"
					className="h-10 w-full text-slate-600"
					onClick={() => {
						set_error(null);
						set_notice(null);
						set_password("");
						set_step("identifier");
					}}
				>
					<ArrowLeft className="size-4" />
					Back to email
				</Button>
			</form>
		);
	}

	if (step === "password-reset") {
		return (
			<form
				onSubmit={handle_password_reset_submit}
				className="space-y-4"
			>
				<AuthError message={error} />
				<AuthNotice message={notice} />
				<div className="space-y-2">
					<div className="flex items-center justify-between gap-3">
						<Label>University email</Label>
						<button
							type="button"
							onClick={() => {
								set_error(null);
								set_notice(null);
								set_password("");
								set_available_auth_methods(["password", "email_code"]);
								set_sso_redirect_to(null);
								set_sso_organization(undefined);
								set_step("identifier");
							}}
							className="text-sm font-semibold text-[#235a96] hover:text-[#1d4d82]"
						>
							Use a different email
						</button>
					</div>
					<div className="flex min-h-11 items-center gap-3 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-base text-slate-900">
						<span className="text-slate-400">
							<Mail className="size-4" />
						</span>
						<span className="min-w-0 truncate">{email}</span>
					</div>
				</div>
				<Button
					type="submit"
					disabled={is_disabled}
					className="h-11 w-full rounded-lg bg-[#235a96] text-white hover:bg-[#1d4d82]"
				>
					{is_submitting ? <Loader2 className="size-4 animate-spin" /> : null}
					Send reset email
				</Button>
				<Button
					type="button"
					variant="ghost"
					className="h-10 w-full text-slate-600"
					onClick={() => {
						set_error(null);
						set_notice(null);
						set_step("password");
					}}
				>
					<ArrowLeft className="size-4" />
					Use password instead
				</Button>
			</form>
		);
	}

	return (
		<form
			onSubmit={handle_magic_submit}
			className="space-y-4"
		>
			<AuthError message={error} />
			<AuthNotice message={notice} />
			<div className="space-y-2">
				<div className="flex items-center justify-between gap-3">
					<Label>University email</Label>
					<button
						type="button"
						onClick={() => {
							set_error(null);
							set_notice(null);
							set_password("");
							set_code("");
							set_available_auth_methods(["password", "email_code"]);
							set_sso_redirect_to(null);
							set_sso_organization(undefined);
							set_step("identifier");
						}}
						className="text-sm font-semibold text-[#235a96] hover:text-[#1d4d82]"
					>
						Use a different email
					</button>
				</div>
				<div className="flex min-h-11 items-center gap-3 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-base text-slate-900">
					<span className="text-slate-400">
						<Mail className="size-4" />
					</span>
					<span className="min-w-0 truncate">{email}</span>
				</div>
			</div>
			<div className="space-y-2">
				<Label htmlFor="magic-code">Email code</Label>
				<div className="relative">
					<FieldIcon>
						<KeyRound className="size-4" />
					</FieldIcon>
					<Input
						id="magic-code"
						type="text"
						inputMode="numeric"
						pattern="[0-9]*"
						maxLength={6}
						value={code}
						onChange={(event) => set_code(normalize_code(event.target.value))}
						autoComplete="one-time-code"
						className="h-11 rounded-lg bg-white pl-10 text-base"
						placeholder="Enter 6 digit code"
						required
					/>
				</div>
			</div>
			<Button
				type="submit"
				disabled={is_disabled || code.length !== 6}
				className="h-11 w-full rounded-lg bg-[#235a96] text-white hover:bg-[#1d4d82]"
			>
				{is_submitting ? <Loader2 className="size-4 animate-spin" /> : null}
				Verify code
			</Button>
			<Button
				type="button"
				variant="outline"
				disabled={is_disabled || resend_seconds > 0}
				onClick={() => request_magic_code()}
				className="h-11 w-full rounded-lg border-slate-300 text-slate-700 hover:bg-slate-50"
			>
				{is_submitting ? <Loader2 className="size-4 animate-spin" /> : <KeyRound className="size-4" />}
				{resend_seconds > 0 ? `Resend code in ${resend_seconds}s` : "Resend code"}
			</Button>
			<Button
				type="button"
				variant="ghost"
				className="h-10 w-full text-slate-600"
				onClick={() => {
					set_error(null);
					set_notice(null);
					set_code("");
					set_step("password");
				}}
			>
				<ArrowLeft className="size-4" />
				Use password instead
			</Button>
		</form>
	);
}
